import { useMutation } from '@tanstack/react-query';

import { api, type ApiRequestConfig } from 'api/v1/api';
import { otpPlatform, type OtpChannel } from 'constants/otp';

export type { OtpChannel };
export type OtpPurpose = 'phone_verify' | 'register' | 'password_reset' | 'personal_data' | 'login';

export type OtpStartResponse = {
  challenge_id: string;
  channel: OtpChannel | string;
  retry_after: number;
  cascade_after: number;
  stub?: boolean;
  message: string;
};

export type OtpVerifyResponse = {
  ok?: boolean;
  purpose?: string;
  reset_token?: string;
  message?: string;
  phone_verified?: boolean;
  session_started?: boolean;
  id?: number;
  email?: string | null;
  phone?: string | null;
  roles?: number[];
  challenge_id?: string;
  has_password?: boolean;
  email_verified?: boolean;
};

const guestConfig = {
  skipAuthRefresh: true,
  skipGlobalErrorHandler: true,
  skipAuthHeader: true,
} as ApiRequestConfig;

export type OtpStatusResponse = {
  channel: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'stub' | 'unknown';
  provider_status?: string | null;
  error?: string | null;
  message: string;
};

export function otpChannelLabel(channel: string | undefined): string {
  if (channel === 'vk') return 'ВКонтакте';
  if (channel === 'sms') return 'SMS';
  if (channel === 'simpush') return 'уведомление';
  if (channel === 'voice' || channel === 'flashcall') return 'звонок';
  if (channel === 'telegram') return 'Telegram';
  return channel || 'неизвестно';
}

/** Provider delivery status of the last send (no phone, no code in the response). */
export async function fetchOtpStatus(challengeId: string, guest = false): Promise<OtpStatusResponse> {
  const { data } = await api.get<OtpStatusResponse>('/otp/status/', {
    params: { challenge_id: challengeId },
    ...(guest ? guestConfig : ({ skipGlobalErrorHandler: true } as ApiRequestConfig)),
  });
  return data;
}

export type PhoneCheckResponse = {
  status: 'ok' | 'not_found' | 'unverified' | 'disabled' | 'available' | 'taken' | 'pending';
  message: string;
};

/** Есть ли аккаунт с номером - до отправки кода. Сервер ничего не отправляет и считает проверки в лимите. */
export async function checkOtpPhone(
  phone: string,
  purpose: 'login' | 'register',
): Promise<PhoneCheckResponse> {
  const { data } = await api.post<PhoneCheckResponse>(
    '/otp/check-phone/',
    { phone, purpose },
    guestConfig,
  );
  return data;
}

export function otpChannelHint(channel: string | undefined): string {
  if (channel === 'vk') return 'Код придет во ВКонтакте.';
  if (channel === 'sms') return 'Код отправлен в SMS.';
  if (channel === 'simpush') {
    return 'Код придет уведомлением на телефон. Если не дойдет - SMS.';
  }
  if (channel === 'voice' || channel === 'flashcall') {
    return 'Позвоним и робот продиктует код. Возьмите трубку.';
  }
  return 'Код отправлен.';
}

function withPlatform<T extends { platform?: 'apple' | 'other' }>(body: T): T {
  return { ...body, platform: body.platform || otpPlatform() };
}

export const useStartOtp = (guest = false) =>
  useMutation({
    mutationFn: async (body: {
      phone?: string;
      purpose: OtpPurpose;
      channel?: OtpChannel;
      platform?: 'apple' | 'other';
    }) => {
      const { data } = await api.post<OtpStartResponse>(
        '/otp/start/',
        withPlatform(body),
        guest ? guestConfig : ({ skipGlobalErrorHandler: true } as ApiRequestConfig),
      );
      return data;
    },
  });

export const useResendOtp = (guest = false) =>
  useMutation({
    mutationFn: async (body: {
      challenge_id?: string;
      channel?: OtpChannel;
      phone?: string;
      purpose?: OtpPurpose;
      platform?: 'apple' | 'other';
    }) => {
      const { data } = await api.post<OtpStartResponse>(
        '/otp/resend/',
        withPlatform(body),
        guest ? guestConfig : ({ skipGlobalErrorHandler: true } as ApiRequestConfig),
      );
      return data;
    },
  });

export const useVerifyOtp = (guest = false) =>
  useMutation({
    mutationFn: async (body: { challenge_id: string; code: string }) => {
      const { data } = await api.post<OtpVerifyResponse>(
        '/otp/verify/',
        body,
        guest ? guestConfig : ({ skipGlobalErrorHandler: true } as ApiRequestConfig),
      );
      return data;
    },
  });
